import { t } from 'i18next';
import { BaseController, BaseState } from '../../../common/controllers/BaseController';
import { QueryParams } from '../../../common/enums/QueryParams';
import { Severity } from '../../../common/enums/Severity';
import { WithRouterProps, withRouter } from '../../../common/higher-order-components/with-router';
import { InventoryCard } from '../../../common/inventory/components/Inventory';
import { InventoryRecap } from '../../../common/inventory/models/InventoryRecap';
import { InventoryService } from '../../../common/inventory/services/InventoryService';
import { UrlHelper } from '../../../common/utils/UrlHelper';
import { ProjectExportState } from '../../../exports/triggers/states/ProjectExportState';
import { ProjectOutletContextType } from '../../common/contexts/ProjectOutletContext';
import { Project } from '../../common/models/Project';
import { FilteredViewToggler } from '../components/FilteredViewToggler';
import { Issues } from '../components/Issues';
import { ProjectDashboardMapper } from '../mappers/ProjectDashboardMapper';
import { Project as DashboardProject } from '../models/Project';
import { Stack } from '@mui/material';

type Props = WithRouterProps<ProjectOutletContextType> & {};

type State = BaseState & {
  inventory: InventoryRecap | null;
  withFilteredView: boolean;
  loadingInventory: boolean;
  loadingProject: boolean;
};

class Controller extends BaseController<Props, State> {
  private readonly inventoryService = new InventoryService();
  private readonly projectDashboardMapper = new ProjectDashboardMapper();

  private readonly projectId: number;
  private project: DashboardProject | null;
  private defaultOpenSeverities: Severity[];
  private focusedIssueNumber: number | null = null;

  private scheduledCleanupTimeout: NodeJS.Timeout | undefined;

  private exportState: ProjectExportState;

  constructor(props: Props, context: unknown) {
    super(props, context);

    ({ projectId: this.projectId } = this.parseNumericIds(['projectId']));
    this.project = null;
    this.exportState = ProjectExportState.getInstance();

    this.defaultOpenSeverities = [];
    const passedInSeverityToView = UrlHelper.getQueryParam(QueryParams.SEVERITY) as Severity | null;
    if (passedInSeverityToView !== null && Object.values(Severity).includes(passedInSeverityToView)) {
      this.defaultOpenSeverities.push(passedInSeverityToView);
    }

    const focusToIssueNumber = Number.parseId(UrlHelper.getQueryParam(QueryParams.ISSUE_NUMBER));
    if (focusToIssueNumber !== null) {
      this.focusedIssueNumber = focusToIssueNumber;
    }

    this.state = {
      ...super.getBaseState(),
      inventory: null,
      loadingInventory: true,
      loadingProject: true,
      withFilteredView: false,
    };
  }

  componentDidMount() {
    super.componentDidMount();

    this.loadProjectContext();
    this.getInventoryForProject();

    // Clear set state so that recurring renders don't reuse the state
    this.scheduledCleanupTimeout = setTimeout(() => {
      this.focusedIssueNumber = null;
      this.defaultOpenSeverities = [];

      this.forceUpdate();
    }, 2000);
  }

  componentDidUpdate(prevProps: Readonly<Props>, prevState: Readonly<State>): void {
    super.componentDidUpdate(prevProps, prevState);

    this.onProjectContextChanged(prevProps.context.project, this.props.context.project);
  }

  componentWillUnmount() {
    super.componentWillUnmount();

    this.inventoryService.cancelAllRequests();

    if (this.scheduledCleanupTimeout) {
      clearTimeout(this.scheduledCleanupTimeout);
    }
  }

  async getInventoryForProject() {
    try {
      const inventory = await this.inventoryService.getProjectInventory(this.projectId);
      this.setState({ inventory, loadingInventory: false });
    } catch (e: unknown) {
      this.setState({ loadingInventory: false });
      this.handleError(e, { invalidUrl: t('nonExistingProject') });
    }
  }

  toggleFilteredView() {
    this.setState(
      (prevState) => ({ ...prevState, withFilteredView: !prevState.withFilteredView }),
      () => this.exportState.setWithExceptions(this.state.withFilteredView)
    );
  }

  render() {
    const { withFilteredView, loadingInventory, loadingProject, inventory } = this.state;
    return this.renderBase(
      <Stack gap={2}>
        <FilteredViewToggler active={withFilteredView} onToggle={() => this.toggleFilteredView()} loading={loadingProject} project={this.project} />
        <Issues
          loading={loadingProject}
          project={this.project}
          withFilteredView={withFilteredView}
          defaultOpenForSeverities={this.defaultOpenSeverities}
          focusOnIssueNumber={this.focusedIssueNumber}
        />
        <InventoryCard
          title={t('projectInventorySummary')}
          subtitle={t('projectInventorySummarySubtitle')}
          loadingData={false}
          initialLoad={loadingInventory}
          inventory={inventory}
        />
      </Stack>
    );
  }

  private loadProjectContext() {
    this.onProjectContextChanged(null, this.props.context.project);
  }

  private onProjectContextChanged(prevProject: Project | null, currProject: Project | null) {
    // Project hasn't changed, do nothing
    if (prevProject === currProject) return;

    if (prevProject === null && currProject !== null) {
      this.project = this.projectDashboardMapper.mapToDashboardProject(currProject);
      this.setState({ loadingProject: false });
    }
  }
}

export const ProjectDashboardController = withRouter<ProjectOutletContextType>(Controller);
